import React, { useState } from "react";
import {
  FileQuestion,
  ChevronDown,
  Mail,
  BookOpen,
  Scale,
  ArrowUpRight,
  Sliders,
  ShieldCheck,
  FileText
} from "lucide-react";
import { DomainPreset } from "../types";
import type { LegalSectionId } from "./LegalHubPage";

interface AcademicInfoSectionProps {
  onLoadSampleIntoEditor?: (sampleText: string, preset?: DomainPreset) => void;
  onNavigateToLegal?: (section: LegalSectionId) => void;
}

interface CaseStudySample {
  id: string;
  title: string;
  domain: string;
  preset: DomainPreset;
  authorshipLabel: string;
  burstinessCv: string;
  shannonEntropy: string;
  herdansC: string;
  zipfFit: string;
  excerpt: string;
  forensicAnalysis: string;
}

const CASE_STUDIES: CaseStudySample[] = [
  {
    id: "human-history",
    title: "19th-Century Maritime Trade & Steamships",
    domain: "History Essay",
    preset: "academic",
    authorshipLabel: "Human Student Essay",
    burstinessCv: "0.68 (High Variety)",
    shannonEntropy: "4.42 (Rich Word Choice)",
    herdansC: "0.87 (Varied)",
    zipfFit: "0.94 (Natural)",
    excerpt:
      "Coal changed everything—not overnight, and certainly not on the tidy schedule that Victorian railway pamphlets promised their investors. Along the Clyde and the Tyne, timber shipwrights kept laying oak keels well into the 1860s because sailing packets could still undercut compound-engine freighters on the long Cape Horn run where coaling stations simply did not exist. Yet once the Suez canal opened in November 1869, the economic calculus flipped within thirty-six months. Short-haul Mediterranean colliers suddenly dictated insurance premiums in London.",
    forensicAnalysis:
      "Notice how the sentence lengths jump naturally from 12 words up to 41 words. The writer mixes quick thoughts with specific historical details ('Clyde', 'November 1869', 'compound-engine freighters')—a clear sign of a real person writing."
  },
  {
    id: "ai-synthesis",
    title: "Cloud Infrastructure & Business Strategy",
    domain: "Corporate Summary",
    preset: "business",
    authorshipLabel: "Unedited AI Output",
    burstinessCv: "0.19 (Very Uniform)",
    shannonEntropy: "3.61 (Predictable)",
    herdansC: "0.74 (Repetitive)",
    zipfFit: "0.78 (Flat)",
    excerpt:
      "In today's rapidly evolving digital landscape, strategic cloud infrastructure allocation constitutes a cornerstone of contemporary organizational maturity. By adhering to structured governance standards and fostering proactive stakeholder engagement, enterprises achieve sustainable competitive advantages while minimizing operational friction. Furthermore, the integration of automated continuous deployment pipelines ensures consistent quality assurance across distributed engineering clusters. Consequently, technological transformation remains harmoniously synchronized with enterprise risk management objectives.",
    forensicAnalysis:
      "Every single sentence is almost the exact same length (18 to 24 words). It leans heavily on textbook filler phrases ('In today's rapidly evolving', 'Furthermore', 'Consequently') and safe, predictable buzzwords typical of AI."
  },
  {
    id: "human-lab",
    title: "Enzyme Reaction Rate Lab Report",
    domain: "Science Lab Report",
    preset: "technical",
    authorshipLabel: "Human STEM Report (False-Positive Check)",
    burstinessCv: "0.41 (Moderate)",
    shannonEntropy: "4.18 (Specific)",
    herdansC: "0.83 (Varied)",
    zipfFit: "0.91 (Natural)",
    excerpt:
      "Absorbance at 240 nm was recorded at 15-second intervals for 180 seconds using a quartz cuvette (path length = 1.0 cm). In Trial 3, substrate depletion plateaued prematurely at t = 90 s because residual condensation on the cuvette wall scattered the UV beam before the blank calibration was repeated. After wiping the optical face with lint-free ethanol tissue, initial velocity (V0) stabilized at 0.042 mM/s across the pH 7.0 phosphate buffer series.",
    forensicAnalysis:
      "Lab reports use formal passive voice, which often fools basic AI detectors. CatchAI Text's Technical preset recognizes real-world lab details ('Trial 3', 'condensation on the cuvette wall', '0.042 mM/s') so science students aren't falsely flagged."
  },
  {
    id: "human-esl",
    title: "Public Bus Subsidies in Growing Cities",
    domain: "International / ESL Student Essay",
    preset: "academic",
    authorshipLabel: "Non-Native English Writer (Fairness Check)",
    burstinessCv: "0.46 (Balanced)",
    shannonEntropy: "3.98 (Clear & Direct)",
    herdansC: "0.81 (Natural)",
    zipfFit: "0.89 (Natural)",
    excerpt:
      "Public bus networks are very important for workers who live far from the central market district. When municipal governments reduce ticket prices by thirty percent, daily commuters can save money for family food and school tuition. However, if the city does not buy additional buses at the same time, morning routes become overcrowded and arrival schedules become unreliable for factory employees.",
    forensicAnalysis:
      "Writers learning English as a second language often use clear, everyday words and helpful connectors ('When', 'However, if'). CatchAI Text includes built-in fairness adjustments so clear, well-structured ESL writing is not mistaken for AI."
  }
];

const STYLOMETRIC_METRICS_TABLE = [
  {
    metric: "Sentence Variety (Burstiness)",
    formula: "Mix of short & long sentences",
    humanRange: "High variety (0.45 – 0.85+)",
    aiRange: "Very uniform (0.14 – 0.32)",
    interpretation:
      "Humans naturally mix short, punchy sentences with longer, detailed ones. AI tends to write sentences that are almost all the same length."
  },
  {
    metric: "Word Unpredictability (Entropy)",
    formula: "How surprising word choices are",
    humanRange: "Creative & varied (4.1 – 4.7)",
    aiRange: "Safe & expected (3.3 – 3.9)",
    interpretation:
      "AI predicts the most likely next word, making its phrasing feel safe and expected. Humans choose more personal, unexpected words."
  },
  {
    metric: "Natural Word Balance (Zipf's Law)",
    formula: "Common vs. rare word balance",
    humanRange: "Natural flow (0.88 – 0.98)",
    aiRange: "Artificial mix (0.70 – 0.85)",
    interpretation:
      "Real human writing follows a natural mix of everyday words and rarer words. AI rewriters and synonym-swapping tools break this natural balance."
  },
  {
    metric: "Vocabulary Richness (Herdan's C)",
    formula: "Unique words vs. total words",
    humanRange: "Rich vocabulary (0.80 – 0.92)",
    aiRange: "More repetitive (0.68 – 0.79)",
    interpretation:
      "Checks how many unique words you use without penalizing shorter essays. AI often recycles the same phrases across paragraphs."
  },
  {
    metric: "Transition Habits",
    formula: "Paragraph starter patterns",
    humanRange: "Natural flow between ideas",
    aiRange: "Heavy textbook signposts",
    interpretation:
      "AI relies heavily on stiff connectors like 'Furthermore,' 'Moreover,' and 'In conclusion' at the start of almost every paragraph."
  },
  {
    metric: "Punctuation Rhythm",
    formula: "Pauses, dashes & list habits",
    humanRange: "Natural pauses (—, ;, ())",
    aiRange: "Repetitive 3-item lists",
    interpretation:
      "Humans use dashes, parentheses, and semicolons the way they speak. AI strongly favors neat, repetitive three-item comma lists."
  }
];

const FAQS = [
  {
    q: "Could my original, hard-earned human writing be falsely flagged as AI?",
    a: "Yes. No AI detector in the world is 100% deterministic. False positives can occur—particularly on highly structured technical summaries, legal briefs, standardized lab reports, and formulaic academic prose. This is why CatchAI Text rejects opaque binary pass/fail accusations, displays probabilistic confidence bands with a dedicated 'Borderline / Mixed Signal' review zone (30%–69%), and exposes sentence-by-sentence stylometric metrics so human evaluators can inspect the exact mathematical basis of any score.",
    category: "accuracy",
    tag: "False Positives"
  },
  {
    q: "Will scanning my document here save it, share it, or trigger a match on Turnitin later?",
    a: "Never. CatchAI Text executes 100% locally inside your web browser using WebAssembly and client-side TypeScript kernels. Zero bytes of your pasted or uploaded essay are transmitted to external detection databases, cloud storage, or third-party AI training repositories. When you close or refresh your browser tab, your document is cleared from volatile memory. Scanning a draft here will never cause a self-plagiarism or repository match in Turnitin, iThenticate, or university LMS portals.",
    category: "privacy",
    tag: "Privacy & Plagiarism"
  },
  {
    q: "What should a student do if an instructor questions their authentic human paper?",
    a: "Gather your verifiable drafting provenance immediately: 1) Export your Google Docs Revision History or Microsoft Word Track Changes log showing chronological keystrokes, intermediate deletions, and multi-day drafting sessions; 2) Present your preliminary research notes, annotated bibliography, and outline drafts; 3) Request an in-person or video conference to walk through your thesis and analytical methodology. CatchAI Text's printable forensic report explicitly states that statistical stylometry is an advisory diagnostic indicator and must never be used as sole evidence for disciplinary penalties.",
    category: "academic",
    tag: "Student Recourse"
  },
  {
    q: "Does using Grammarly, spell-check, or light editing make my paper look like AI?",
    a: "Standard orthography checks, spelling corrections, and fixing isolated punctuation errors do not meaningfully alter your sentence length burstiness or Herdan's C vocabulary richness. However, using generative full-sentence rewrite tools ('Rewrite entire paragraph for clarity' or 'Make sound more academic') replaces your natural syntactic cadence with low-entropy, uniform machine phrasing. To preserve your authentic voice, use proofreading tools strictly for grammar and typo correction rather than wholesale sentence generation.",
    category: "tools",
    tag: "Writing Tools"
  },
  {
    q: "Are non-native English speakers (ESL / EFL writers) unfairly penalized by AI detectors?",
    a: "Peer-reviewed research (including Stanford University's evaluation of commercial AI detectors) demonstrates that naive perplexity detectors disproportionately misclassify non-native English writers because L2 students often rely on foundational core vocabulary and structured curricular transition phrases. CatchAI Text mitigates this bias by combining sentence-length burstiness with Zipfian rank distribution and an ESL calibration offset, and instructs educators to evaluate linguistic background holistically.",
    category: "academic",
    tag: "ESL & Bias"
  },
  {
    q: "Can an instructor or institution fail or discipline a student based solely on an AI score?",
    a: "Ethically, scientifically, and legally under frameworks such as the EU AI Act (Annex III for High-Risk Educational Assessments), no automated statistical score can serve as standalone proof of academic misconduct. CatchAI Text is engineered as a transparent pedagogical instrument to initiate constructive dialogue and formative writing review, never to automate grading sanctions.",
    category: "academic",
    tag: "Institutional Policy"
  },
  {
    q: "Can AI paraphrasers, 'humanizers', or synonym swappers bypass stylometric analysis?",
    a: "Simple synonym-replacement spinners often substitute rare words into rigid machine sentence skeletons. While this may fool basic keyword detectors, it typically distorts the natural Zipf's Law power-law distribution (R²) and fails to reproduce authentic human clause-length burstiness across multi-paragraph arguments. CatchAI Text evaluates 10 orthogonal dimensions simultaneously to detect unnatural syntactic uniformity.",
    category: "accuracy",
    tag: "Paraphrasers & Bypass"
  },
  {
    q: "How does 100% client-side detection work without sending text to a cloud GPU server?",
    a: "Unlike deep neural perplexity models that require gigabytes of server VRAM, computational stylometry computes closed-form statistical distributions—such as token frequency tables, logarithmic type-token ratios, bigram Markov transition matrices, and sentence length variance—directly on your device CPU in under 50 milliseconds. This architecture guarantees complete FERPA/GDPR data sovereignty and full offline functionality.",
    category: "privacy",
    tag: "Offline Architecture"
  }
];

export default function AcademicInfoSection({
  onLoadSampleIntoEditor,
  onNavigateToLegal
}: AcademicInfoSectionProps) {
  const [activeGuideTab, setActiveGuideTab] = useState<"methodology" | "cases" | "protocol">("methodology");
  const [openFaqIndices, setOpenFaqIndices] = useState<Record<number, boolean>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const toggleFaq = (idx: number) => {
    setOpenFaqIndices((prev) => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const filteredFaqs = FAQS.map((faq, originalIdx) => ({ ...faq, originalIdx })).filter(
    (f) => selectedCategory === "all" || f.category === selectedCategory
  );

  return (
    <div className="flex flex-col gap-10" id="methodology-and-faq-panel">
      {/* SECTION 1: Plain-English Guide to How Detection Works & Sample Examples */}
      <section
        aria-labelledby="stylometric-research-heading"
        className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl p-5 sm:p-8 flex flex-col gap-6 border border-slate-200/90 dark:border-slate-800"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>How Detection Works</span>
              <span aria-hidden="true">·</span>
              <span>Plain-English Guide</span>
              <span aria-hidden="true">·</span>
              <span>100% Private in Your Browser</span>
            </div>
            <h2
              id="stylometric-research-heading"
              className="font-display font-bold text-xl sm:text-2xl text-slate-900 dark:text-white tracking-tight"
            >
              How CatchAI Text Tells Human Writing Apart from AI
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Instead of giving you an unexplained mystery score, CatchAI Text checks how your sentences are built—right inside your browser. Explore what we look for in plain English, test real writing examples, or see how teachers and students should read the results.
            </p>
          </div>

          {/* Interactive Guide Mode Switcher */}
          <div
            className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800/90 rounded-xl border border-slate-200/70 dark:border-slate-700/70 self-start shrink-0 overflow-x-auto max-w-full"
            role="tablist"
            aria-label="How detection works sections"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeGuideTab === "methodology"}
              onClick={() => setActiveGuideTab("methodology")}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5 ${
                activeGuideTab === "methodology"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xs"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>1. What We Measure</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeGuideTab === "cases"}
              onClick={() => setActiveGuideTab("cases")}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5 ${
                activeGuideTab === "cases"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xs"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>2. Sample Examples</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeGuideTab === "protocol"}
              onClick={() => setActiveGuideTab("protocol")}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5 ${
                activeGuideTab === "protocol"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xs"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              <Scale className="w-3.5 h-3.5" />
              <span>3. Fair Review Guide</span>
            </button>
          </div>
        </div>

        {/* TAB 1: What We Measure Table */}
        <div
          className={activeGuideTab === "methodology" ? "block space-y-5" : "hidden"}
          role="tabpanel"
          aria-label="What We Measure Reference"
        >
          <div className="overflow-x-auto border border-slate-200/80 dark:border-slate-800 rounded-xl">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-850/60 text-slate-700 dark:text-slate-300 font-semibold">
                  <th className="py-3 px-4">Writing Signal</th>
                  <th className="py-3 px-4">What It Checks</th>
                  <th className="py-3 px-4 font-mono tabular-nums">Typical Human Writing</th>
                  <th className="py-3 px-4 font-mono tabular-nums">Typical AI Writing</th>
                  <th className="py-3 px-4">Why It Matters in Plain English</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/80 text-slate-700 dark:text-slate-300">
                {STYLOMETRIC_METRICS_TABLE.map((row) => (
                  <tr key={row.metric} className="hover:bg-slate-50/50 dark:hover:bg-slate-850/30 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-white whitespace-nowrap">
                      {row.metric}
                    </td>
                    <td className="py-3.5 px-4 text-xs text-indigo-700 dark:text-indigo-300 font-medium whitespace-nowrap">
                      {row.formula}
                    </td>
                    <td className="py-3.5 px-4 font-mono tabular-nums text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                      {row.humanRange}
                    </td>
                    <td className="py-3.5 px-4 font-mono tabular-nums text-amber-700 dark:text-amber-400 whitespace-nowrap">
                      {row.aiRange}
                    </td>
                    <td className="py-3.5 px-4 text-xs leading-relaxed text-slate-600 dark:text-slate-400 min-w-[260px]">
                      {row.interpretation}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
                Why Sentence Rhythm Beats Word Lists
              </h3>
              <p>
                Flagging trendy words like &ldquo;delve&rdquo; or &ldquo;tapestry&rdquo; unfairly blames good student writers. Checking sentence rhythm across whole paragraphs is a much fairer way to see how a real person naturally thinks and writes.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
                Built for Different Writing Styles
              </h3>
              <p>
                A science lab report naturally sounds more formal than a personal story. Choosing the right preset (Academic, Technical, Creative, or Business) adjusts the detector so formal writing isn&apos;t falsely flagged.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
                100% Private on Your Device
              </h3>
              <p>
                Every check runs directly inside your web browser. Your essays, notes, and drafts are never uploaded to external servers or stored in any plagiarism database.
              </p>
            </div>
          </div>
        </div>

        {/* TAB 2: Sample Examples */}
        <div
          className={activeGuideTab === "cases" ? "block space-y-6" : "hidden"}
          role="tabpanel"
          aria-label="Sample Writing Examples"
        >
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
            See how four real-world writing examples compare. Click <strong>Load Sample into Editor</strong> on any card to test it live in the detector above.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {CASE_STUDIES.map((cs) => (
              <article
                key={cs.id}
                className="border border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex flex-col justify-between gap-4 bg-slate-50/40 dark:bg-slate-950/30"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-slate-500 dark:text-slate-400">
                    <span>{cs.domain}</span>
                    <span aria-hidden="true">·</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{cs.authorshipLabel}</span>
                  </div>

                  <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                    {cs.title}
                  </h3>

                  <blockquote className="text-xs sm:text-[13px] text-slate-700 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-800 italic">
                    &ldquo;{cs.excerpt}&rdquo;
                  </blockquote>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 font-mono tabular-nums text-[11px]">
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800">
                      <div className="text-slate-400">Sentence Mix</div>
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{cs.burstinessCv}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800">
                      <div className="text-slate-400">Word Variety</div>
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{cs.shannonEntropy}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800">
                      <div className="text-slate-400">Vocabulary</div>
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{cs.herdansC}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800">
                      <div className="text-slate-400">Natural Flow</div>
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{cs.zipfFit}</div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    <strong className="text-slate-900 dark:text-slate-200">Why It Scores This Way:</strong> {cs.forensicAnalysis}
                  </p>
                </div>

                {onLoadSampleIntoEditor && (
                  <div className="pt-2 border-t border-slate-200/70 dark:border-slate-800 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onLoadSampleIntoEditor(cs.excerpt, cs.preset)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      <span>Load Sample into Editor</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* TAB 3: Fair Review Guide */}
        <div
          className={activeGuideTab === "protocol" ? "block space-y-6" : "hidden"}
          role="tabpanel"
          aria-label="Fair Review Guide for Teachers and Students"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-950/30 space-y-2.5">
              <div className="text-xs font-mono text-indigo-600 dark:text-indigo-400 font-semibold">
                01. Before You Scan
              </div>
              <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                Use Enough Text &amp; Pick the Right Style
              </h3>
              <p className="text-xs leading-relaxed">
                A single sentence isn&apos;t enough to judge someone&apos;s writing style fairly. While 15 words unlocks a basic check, we recommend scanning at least 150 words (two full paragraphs) and choosing the matching preset (Academic, Technical, Creative, or Business).
              </p>
            </div>

            <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-950/30 space-y-2.5">
              <div className="text-xs font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                02. If a Paper Is Flagged
              </div>
              <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                Check Version History &amp; Rough Drafts
              </h3>
              <p className="text-xs leading-relaxed">
                If a paper lands in the Mixed (30%–69%) or Likely AI (70%+) range, check the student&apos;s Google Docs or Microsoft Word version history. Authentic human writing shows natural edits, rewrites, and progress built up over time.
              </p>
            </div>

            <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-950/30 space-y-2.5">
              <div className="text-xs font-mono text-amber-600 dark:text-amber-400 font-semibold">
                03. Protecting Students
              </div>
              <h3 className="font-display font-bold text-base text-slate-900 dark:text-white">
                Never Grade or Penalize by Score Alone
              </h3>
              <p className="text-xs leading-relaxed">
                No AI detector is 100% certain, and non-native English (ESL) or technical writers often use structured patterns. Always treat the score as a starting point for a conversation with the student—never as automatic proof of misconduct.
              </p>
            </div>
          </div>
        </div>

        {onNavigateToLegal && (
          <nav
            aria-label="Core Documentation and Policy Pages"
            className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 dark:border-slate-800 text-xs"
          >
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Official guides &amp; policies:</span>
            </div>
            <div className="flex items-center gap-3.5 flex-wrap">
              <a
                href="/about"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("about");
                }}
                className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                About &amp; Methodology
              </a>
              <span aria-hidden="true" className="text-slate-300 dark:text-slate-700">·</span>
              <a
                href="/score-guide"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("score-guide");
                }}
                className="font-semibold text-slate-700 dark:text-slate-300 hover:underline"
              >
                Score Guide
              </a>
              <span aria-hidden="true" className="text-slate-300 dark:text-slate-700">·</span>
              <a
                href="/ethics"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("ethics");
                }}
                className="font-semibold text-slate-700 dark:text-slate-300 hover:underline"
              >
                AI Ethics
              </a>
              <span aria-hidden="true" className="text-slate-300 dark:text-slate-700">·</span>
              <a
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("privacy");
                }}
                className="font-semibold text-slate-700 dark:text-slate-300 hover:underline"
              >
                Privacy Policy
              </a>
              <span aria-hidden="true" className="text-slate-300 dark:text-slate-700">·</span>
              <a
                href="/terms"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("terms");
                }}
                className="font-semibold text-slate-700 dark:text-slate-300 hover:underline"
              >
                Terms of Service
              </a>
              <span aria-hidden="true" className="text-slate-300 dark:text-slate-700">·</span>
              <a
                href="/dmca"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToLegal("dmca");
                }}
                className="font-semibold text-slate-700 dark:text-slate-300 hover:underline"
              >
                DMCA Policy
              </a>
            </div>
          </nav>
        )}
      </section>

      {/* SECTION 2: Frequently Asked Questions (All Answers Always Mounted in DOM for Crawlers) */}
      <section
        aria-labelledby="faq-section-heading"
        className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl p-5 sm:p-8 flex flex-col gap-6 border border-slate-200/90 dark:border-slate-800"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 flex items-center justify-center shrink-0">
                <FileQuestion className="w-4 h-4" />
              </div>
              <h2
                id="faq-section-heading"
                className="font-display font-bold text-lg sm:text-xl text-slate-900 dark:text-white tracking-tight"
              >
                Frequently Asked Questions
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Category Filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 shrink-0">
              {[
                { id: "all", label: "All Questions" },
                { id: "accuracy", label: "Accuracy" },
                { id: "privacy", label: "Privacy" },
                { id: "academic", label: "Academic Use" },
                { id: "tools", label: "Grammar Tools" }
              ].map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory === cat.id
                      ? "bg-slate-900 text-white dark:bg-white dark:text-slate-950"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200/80 dark:hover:bg-slate-750"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Accordion Questions List - Answers remain in DOM for SEO & AdSense Content Evaluation */}
        <div className="flex flex-col gap-3">
          {filteredFaqs.map((faq) => {
            const isOpen = Boolean(openFaqIndices[faq.originalIdx]);
            return (
              <div
                key={faq.originalIdx}
                className={`border rounded-xl sm:rounded-2xl overflow-hidden transition-all ${
                  isOpen
                    ? "border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-850/40"
                    : "border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(faq.originalIdx)}
                  className="w-full text-left p-4 sm:p-5 flex items-center justify-between gap-4 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="leading-snug text-slate-900 dark:text-slate-100 font-semibold">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${
                      isOpen ? "rotate-180 text-slate-700 dark:text-slate-200" : ""
                    }`}
                  />
                </button>
                <div
                  className={
                    isOpen
                      ? "px-5 pb-5 pt-2 text-xs sm:text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-100 dark:border-slate-800/60 block"
                      : "hidden"
                  }
                >
                  {faq.a}
                </div>
              </div>
            );
          })}
        </div>

        {/* Support Desk Callout */}
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-500 shrink-0" />
            <span>Need institutional academic support, curriculum rubrics, or have an unanswered question?</span>
          </div>
          <a
            href="mailto:catchaitext@gmail.com"
            className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1.5 shrink-0"
          >
            <span>Email Support: catchaitext@gmail.com</span>
          </a>
        </div>
      </section>
    </div>
  );
}
